export type DocumentOrigin = "GENERIC" | "EXISTING";

export type RelatedDocumentSource =
  | "TRANSACTION"
  | "BOARD_MEETING"
  | "DIRECTOR"
  | "ONBOARDING"
  | "COMPANY_LINK"
  | "LEGACY_COMPANY";

export type RelatedDocumentFilter = "ALL" | RelatedDocumentSource;

export type DocumentType = "FILE" | "LINK";

export interface CompanyDocumentFolder {
  id: string;
  name: string;
  parentId: string | null;
  updatedAt: string;
  fileCount?: number;
  totalSize?: string;
}

export interface CompanyDocumentBreadcrumb {
  id: string | null;
  name: string;
}

export interface CompanyDocumentUploadedBy {
  id: number;
  name: string;
}

export interface GenericCompanyDocument {
  id: string;
  name: string;
  originalName?: string;
  size: string;
  mimeType: string;
  updatedAt: string;
  uploadedBy?: CompanyDocumentUploadedBy | null;
  source: "GENERIC";
  readOnly: false;
  downloadable: boolean;
}

export interface CompanyDocumentBrowserResponse {
  currentFolder: CompanyDocumentFolder | null;
  breadcrumb: CompanyDocumentBreadcrumb[];
  folders: CompanyDocumentFolder[];
  files: GenericCompanyDocument[];
}

export type CompanyDocumentFileTypeFilter =
  | "ALL"
  | "PDF"
  | "SPREADSHEET"
  | "DOCUMENT"
  | "PRESENTATION"
  | "IMAGE"
  | "TEXT";

export type CompanyDocumentSearchSortBy = "updatedAt" | "name" | "size";
export type CompanyDocumentSearchSortOrder = "asc" | "desc";

export interface CompanyDocumentSearchFolder extends CompanyDocumentFolder {
  breadcrumb: CompanyDocumentBreadcrumb[];
}

export interface CompanyDocumentSearchFile extends GenericCompanyDocument {
  folder: {
    id: string;
    name: string;
    breadcrumb: CompanyDocumentBreadcrumb[];
  } | null;
}

export interface CompanyDocumentSearchRequest {
  q?: string;
  folderId?: string;
  fileType?: CompanyDocumentFileTypeFilter;
  uploadedBy?: number;
  updatedFrom?: string;
  updatedTo?: string;
  sortBy?: CompanyDocumentSearchSortBy;
  sortOrder?: CompanyDocumentSearchSortOrder;
  page?: number;
  pageSize?: number;
}

export interface CompanyDocumentSearchResponse {
  query?: string;
  filters?: Record<string, unknown>;
  folders: CompanyDocumentSearchFolder[];
  files: CompanyDocumentSearchFile[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface RelatedCompanyDocument {
  id: string;
  name: string;
  source: RelatedDocumentSource;
  sourceLabel: string;
  contextId?: string | null;
  contextLabel?: string | null;
  documentType: DocumentType;
  size?: string | null;
  updatedAt: string;
  readOnly: true;
  downloadable: boolean;
  url?: string | null;
  externalUrl?: string | null;
}

export interface CreateCompanyDocumentFolderRequest {
  parentId: string | null;
  name: string;
}

export interface PresignCompanyDocumentFile {
  clientId: string;
  name: string;
  contentType: string;
  size: number;
}

export interface PresignCompanyDocumentUploadsRequest {
  folderId: string | null;
  files: PresignCompanyDocumentFile[];
}

export interface PresignedCompanyDocumentUpload {
  clientId: string;
  documentId: string;
  uploadUrl: string;
  completionToken: string;
  expiresIn: number;
  headers: Record<string, string>;
}

export interface PresignCompanyDocumentUploadsResponse {
  uploads: PresignedCompanyDocumentUpload[];
}

export interface CompleteCompanyDocumentUpload {
  documentId: string;
  completionToken: string;
}

export interface CompleteCompanyDocumentUploadsRequest {
  uploads: CompleteCompanyDocumentUpload[];
}

export interface CompleteCompanyDocumentUploadResult {
  documentId: string;
  status?: "READY" | "FAILED";
  success?: boolean;
  error?: string;
  document?: GenericCompanyDocument;
  file?: GenericCompanyDocument;
}

export interface CompleteCompanyDocumentUploadsResponse {
  results?: CompleteCompanyDocumentUploadResult[];
  uploads?: CompleteCompanyDocumentUploadResult[];
}

export interface CompanyDocumentDownloadItem {
  origin: DocumentOrigin;
  id: string;
}

export interface CompanyDocumentDownloadUrlRequest
  extends CompanyDocumentDownloadItem {}

export interface CompanyDocumentDownloadUrlResponse {
  downloadUrl?: string;
  url?: string;
  fileName?: string;
  expiresIn?: number;
}

export interface QueueCompanyDocumentDownloadRequest {
  items: CompanyDocumentDownloadItem[];
}

export interface QueueCompanyDocumentDownloadResponse {
  jobId: string;
  status: "QUEUED";
  itemCount: number;
  totalSize: string;
  archiveFileName: string;
  createdAt: string;
}

export type CompanyDocumentDeleteItem = {
  type: "FILE" | "FOLDER";
  id: string;
};

export interface DeleteCompanyDocumentItemsRequest {
  items: CompanyDocumentDeleteItem[];
}

export interface DeleteCompanyDocumentItemsResponse {
  deleted: {
    requestedFileIds: string[];
    requestedFolderIds: string[];
    fileCount: number;
    folderCount: number;
  };
}

export interface MoveCompanyDocumentFilesRequest {
  fileIds: string[];
  folderId: string | null;
}

export interface MoveCompanyDocumentFilesResponse {
  movedCount: number;
  folderId: string | null;
}

export type VaultDownloadNotificationStatus =
  | "READY"
  | "FAILED"
  | "QUEUED"
  | "PROCESSING";

export interface VaultDownloadNotification {
  id: string;
  jobId?: string;
  status: VaultDownloadNotificationStatus;
  title: string;
  message: string;
  fileName?: string;
  downloadUrl?: string;
  createdAt: string;
  expiresAt?: string;
  readAt?: string | null;
  isRead: boolean;
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | undefined {
  if (typeof value === "string" && value.length > 0) return value;
  if (typeof value === "number") return String(value);
  return undefined;
}

function getRecordValue(
  record: UnknownRecord,
  key: string
): unknown | undefined {
  return Object.prototype.hasOwnProperty.call(record, key)
    ? record[key]
    : undefined;
}

/**
 * Normalizes the durable notification response and the matching SSE payload.
 * The API intentionally permits the event to wrap the notification in either
 * `notification` or `data`, which makes reconnects and backend event upgrades
 * safe for the UI.
 */
export function normalizeVaultDownloadNotification(
  value: unknown
): VaultDownloadNotification | null {
  if (!isRecord(value)) return null;

  const directNotification = getRecordValue(value, "notification");
  const directData = getRecordValue(value, "data");
  const candidate = isRecord(directNotification)
    ? directNotification
    : isRecord(directData)
    ? isRecord(getRecordValue(directData, "notification"))
      ? (getRecordValue(directData, "notification") as UnknownRecord)
      : directData
    : value;

  const eventData = getRecordValue(candidate, "eventData");
  const metadata = getRecordValue(candidate, "metadata");
  const detail = isRecord(eventData)
    ? eventData
    : isRecord(metadata)
    ? metadata
    : {};
  const eventType =
    asString(getRecordValue(value, "eventType")) ||
    asString(getRecordValue(value, "type")) ||
    asString(getRecordValue(candidate, "eventType")) ||
    asString(getRecordValue(candidate, "type")) ||
    asString(getRecordValue(candidate, "status")) ||
    "";
  const rawStatus =
    asString(getRecordValue(candidate, "status")) || eventType || "QUEUED";
  const status: VaultDownloadNotificationStatus = /FAIL|ERROR/i.test(rawStatus)
    ? "FAILED"
    : /READY|SUCCESS|COMPLETE/i.test(rawStatus)
    ? "READY"
    : /PROCESS/i.test(rawStatus)
    ? "PROCESSING"
    : "QUEUED";
  const id =
    asString(getRecordValue(candidate, "id")) ||
    asString(getRecordValue(candidate, "notificationId")) ||
    asString(getRecordValue(candidate, "jobId"));

  if (!id) return null;

  const fileName =
    asString(getRecordValue(candidate, "fileName")) ||
    asString(getRecordValue(candidate, "archiveFileName")) ||
    asString(getRecordValue(detail, "fileName"));
  const downloadUrl =
    asString(getRecordValue(candidate, "downloadUrl")) ||
    asString(getRecordValue(candidate, "url")) ||
    asString(getRecordValue(detail, "downloadUrl")) ||
    asString(getRecordValue(detail, "url"));
  const defaultTitle =
    status === "READY"
      ? "Your document download is ready"
      : status === "FAILED"
      ? "Your document download could not be prepared"
      : "Your document download is being prepared";

  return {
    id,
    jobId:
      asString(getRecordValue(candidate, "jobId")) ||
      asString(getRecordValue(detail, "jobId")),
    status,
    title:
      asString(getRecordValue(candidate, "title")) ||
      asString(getRecordValue(candidate, "subject")) ||
      defaultTitle,
    message:
      asString(getRecordValue(candidate, "message")) ||
      asString(getRecordValue(candidate, "errorMessage")) ||
      asString(getRecordValue(detail, "message")) ||
      (fileName
        ? `${fileName}${status === "READY" ? " is ready to download and available for 24 hours." : ""}`
        : defaultTitle),
    fileName,
    downloadUrl,
    createdAt:
      asString(getRecordValue(candidate, "createdAt")) ||
      asString(getRecordValue(candidate, "timestamp")) ||
      new Date().toISOString(),
    expiresAt:
      asString(getRecordValue(candidate, "expiresAt")) ||
      asString(getRecordValue(candidate, "expiry")) ||
      asString(getRecordValue(detail, "expiresAt")),
    readAt:
      asString(getRecordValue(candidate, "readAt")) ||
      asString(getRecordValue(candidate, "readOn")) ||
      null,
    isRead:
      getRecordValue(candidate, "isRead") === true ||
      Boolean(getRecordValue(candidate, "readAt")),
  };
}
